'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { motion } from 'motion/react';
import { RoomAudioRenderer, RoomContext, StartAudio } from '@livekit/components-react';
import { ErrorMessage } from '@/components/embed-popup/error-message';
import { PopupView } from '@/components/embed-popup/popup-view';
import { Trigger } from '@/components/embed-popup/trigger';
import { ErrorBoundary } from '@/components/error-boundary';
import useConnectionDetails from '@/hooks/use-connection-details';
import { cn } from '@/lib/utils';
import { DEFAULT_WIDGET_POSITION, PANEL_CARD_ALIGN_CLASSES, PANEL_POSITION_CLASSES, isTop, } from '@/lib/widget-position';
const PopupViewMotion = motion.create(PopupView);
function AgentClient({ appConfig, widgetConfig }) {
    var _a;
    const isAnimating = useRef(false);
    const constraintsRef = useRef(null);
    const room = useMemo(() => new Room(), []);
    const [popupOpen, setPopupOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [dragKey, setDragKey] = useState(0);
    const [error, setError] = useState(null);
    const effectiveAppConfig = useMemo(() => {
        var _a;
        const agentName = (_a = widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.agentName) !== null && _a !== void 0 ? _a : appConfig.agentName;
        if (agentName !== appConfig.agentName) {
            return Object.assign(Object.assign({}, appConfig), { agentName });
        }
        return appConfig;
    }, [appConfig, widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.agentName]);
    const { connectionDetails, refreshConnectionDetails, existingOrRefreshConnectionDetails } = useConnectionDetails(effectiveAppConfig);
    const handleTogglePopup = () => {
        if (isAnimating.current)
            return;
        if (!popupOpen) {
            setDragKey((k) => k + 1);
        }
        setError(null);
        setIsCollapsed(false);
        setPopupOpen((open) => !open);
    };
    const handleCollapse = () => {
        setIsCollapsed(true);
    };
    const handleExpand = () => {
        setIsCollapsed(false);
    };
    const handleDisconnect = () => {
        setIsCollapsed(false);
        setPopupOpen(false);
        setError(null);
        room.disconnect();
    };
    const handlePanelAnimationStart = () => {
        isAnimating.current = true;
    };
    const handlePanelAnimationComplete = () => {
        isAnimating.current = false;
        if (!popupOpen && !isCollapsed && room.state !== 'disconnected') {
            room.disconnect();
        }
    };
    useEffect(() => {
        const onDisconnected = () => {
            setPopupOpen(false);
            setIsCollapsed(false);
            refreshConnectionDetails();
        };
        const onMediaDevicesError = (deviceError) => {
            setError({
                title: 'Encountered an error with your media devices',
                description: `${deviceError.name}: ${deviceError.message}`,
            });
        };
        room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.Disconnected, onDisconnected);
            room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        };
    }, [room, refreshConnectionDetails]);
    useEffect(() => {
        if (!popupOpen)
            return;
        if (room.state !== 'disconnected')
            return;
        const connect = async () => {
            try {
                const details = await existingOrRefreshConnectionDetails();
                await Promise.all([
                    room.localParticipant.setMicrophoneEnabled(true, undefined, {
                        preConnectBuffer: appConfig.isPreConnectBufferEnabled,
                    }),
                    room.connect(details.serverUrl, details.participantToken),
                ]);
            }
            catch (connectError) {
                if (connectError instanceof Error) {
                    console.error('Error connecting to agent:', connectError);
                    setError({
                        title: 'There was an error connecting to the agent',
                        description: `${connectError.name}: ${connectError.message}`,
                    });
                }
            }
        };
        connect();
    }, [room, popupOpen, existingOrRefreshConnectionDetails, appConfig.isPreConnectBufferEnabled]);
    const accentColor = widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.themeColor;
    const position = (_a = widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.position) !== null && _a !== void 0 ? _a : DEFAULT_WIDGET_POSITION;
    // The panel slides in from the edge it is anchored to: up from the bottom, down from the top.
    const panelSlideOffset = isTop(position) ? -8 : 8;
    const widgetTheme = (widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.themeMode) === 'light'
        ? 'light'
        : (widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.themeMode) === 'dark'
            ? 'dark'
            : undefined;
    const accentOverrides = useMemo(() => {
        if (!accentColor)
            return undefined;
        return {
            '--fgAccent': accentColor,
            '--primary': accentColor,
            '--primary-hover': accentColor,
            '--bgAccentPrimary': `color-mix(in srgb, ${accentColor} 15%, transparent)`,
            '--bgAccent': `color-mix(in srgb, ${accentColor} 10%, transparent)`,
            '--separatorAccent': `color-mix(in srgb, ${accentColor} 30%, transparent)`,
        };
    }, [accentColor]);
    return (_jsx(ErrorBoundary, { children: _jsxs(RoomContext.Provider, { value: room, children: [_jsx(RoomAudioRenderer, {}), _jsx(StartAudio, { label: "Start Audio" }), _jsx(Trigger, { error: error, popupOpen: popupOpen, isCollapsed: isCollapsed, accentColor: accentColor, logoUrl: widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.logoUrl, position: position, onToggle: handleTogglePopup, onExpand: handleExpand, onDisconnect: handleDisconnect }), _jsx("div", { ref: constraintsRef, className: "pointer-events-none fixed inset-1 z-50", children: _jsxs(motion.div, { drag: true, dragConstraints: constraintsRef, dragElastic: 0.1, dragMomentum: false, whileDrag: { cursor: 'grabbing' }, inert: !popupOpen || isCollapsed, initial: { opacity: 0, translateY: panelSlideOffset }, animate: {
                            opacity: popupOpen && !isCollapsed ? 1 : 0,
                            translateY: popupOpen && !isCollapsed ? 0 : panelSlideOffset,
                        }, transition: {
                            type: 'spring',
                            bounce: 0,
                            duration: popupOpen && !isCollapsed ? 1 : 0.2,
                        }, onAnimationStart: handlePanelAnimationStart, onAnimationComplete: handlePanelAnimationComplete, "data-widget-theme": widgetTheme, style: accentOverrides, className: cn('pointer-events-auto absolute touch-none', PANEL_POSITION_CLASSES[position]), children: [_jsx("div", { className: cn('bg-bg1 dark:bg-bg2 border-separator1 dark:border-separator2 h-[480px] w-full rounded-[28px] border border-solid drop-shadow-md md:w-[360px]', PANEL_CARD_ALIGN_CLASSES[position]), children: _jsxs("div", { className: "relative h-full w-full", children: [_jsx(ErrorMessage, { error: error }), !error && (_jsx(PopupViewMotion, { appConfig: appConfig, initial: { opacity: 1 }, animate: { opacity: error === null ? 1 : 0 }, transition: { type: 'linear', duration: 0.2 }, disabled: !popupOpen, sessionStarted: popupOpen, onEmbedError: setError, onCollapse: handleCollapse, widgetConfig: widgetConfig, className: "absolute inset-0" }))] }) }), (widgetConfig === null || widgetConfig === void 0 ? void 0 : widgetConfig.poweredByText) && (_jsx("div", { className: "mt-1.5 flex justify-center", children: widgetConfig.poweredByUrl ? (_jsx("a", { href: widgetConfig.poweredByUrl, target: "_blank", rel: "noopener noreferrer", className: "text-fg3/60 text-[10px] hover:underline", children: widgetConfig.poweredByText })) : (_jsx("span", { className: "text-fg3/60 text-[10px]", children: widgetConfig.poweredByText })) }))] }, dragKey) })] }) }));
}
export default AgentClient;
